import { useMemo, createContext, useContext, useState, useCallback, useEffect } from 'react'
import { ThemeProvider as MuiThemeProvider, createTheme, alpha } from '@mui/material/styles'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { useTranslation } from 'react-i18next'
import dayjs from 'dayjs'
import 'dayjs/locale/ar'
import 'dayjs/locale/ca'
import 'dayjs/locale/de'
import 'dayjs/locale/es'
import 'dayjs/locale/eu'
import 'dayjs/locale/fr'
import 'dayjs/locale/nl'
import 'dayjs/locale/pl'
import 'dayjs/locale/pt'
import 'dayjs/locale/zh'

const THEME_MODE_STORAGE_KEY = 'themeMode'
export const THEME_MODES = ['auto', 'light', 'dark']

// Sidebar palette tuned for dark mode
const darkSidebarColors = {
    bg: '#0F1214', // near-black con tinte frío (Linear/Vercel style)
    text: 'rgb(212, 219, 231)',
    activeText: '#F0F2F4', // blanco cálido, no puro
    activeBg: 'rgba(255,255,255,0.09)',
    hoverBg: 'rgba(255,255,255,0.05)',
    dividerColor: 'rgba(255,255,255,0.07)',
    scrollThumb: 'rgba(255,255,255,0.15)',
    scrollThumbHover: 'rgba(255,255,255,0.28)'
}

// Sidebar palette tuned for light mode
const lightSidebarColors = {
    bg: '#f4f4f5',
    text: 'rgba(0,0,0,0.55)',
    activeText: 'rgba(0,0,0,0.87)',
    activeBg: 'rgba(0,0,0,0.08)',
    hoverBg: 'rgba(0,0,0,0.04)',
    dividerColor: 'rgba(0,0,0,0.1)',
    scrollThumb: 'rgba(0,0,0,0.18)',
    scrollThumbHover: 'rgba(0,0,0,0.32)'
}

const getStoredThemeMode = () => {
    if (typeof window === 'undefined') return 'auto'
    const stored = window.localStorage?.getItem(THEME_MODE_STORAGE_KEY)
    return THEME_MODES.includes(stored) ? stored : 'auto'
}

const getSystemMode = () => {
    if (typeof window === 'undefined' || !window.matchMedia) return 'light'
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

const ThemeModeContext = createContext({
    sidebarColors: lightSidebarColors,
    themeMode: 'auto',
    resolvedMode: 'light',
    setThemeMode: () => {}
})

export const useFlavorTheme = () => useContext(ThemeModeContext)

export const ThemeProvider = ({ children }) => {
    const { i18n } = useTranslation()
    const [themeMode, setThemeModeState] = useState(getStoredThemeMode)
    const [systemMode, setSystemMode] = useState(getSystemMode)

    // Sync dayjs locale so startOf('week') and DatePicker calendars respect the active language
    useEffect(() => {
        dayjs.locale(i18n.language || 'en')
    }, [i18n.language])

    useEffect(() => {
        if (typeof window === 'undefined' || !window.matchMedia) return undefined
        const mql = window.matchMedia('(prefers-color-scheme: dark)')
        const handler = (e) => setSystemMode(e.matches ? 'dark' : 'light')
        if (mql.addEventListener) mql.addEventListener('change', handler)
        else mql.addListener(handler)
        return () => {
            if (mql.removeEventListener) mql.removeEventListener('change', handler)
            else mql.removeListener(handler)
        }
    }, [])

    const resolvedMode = themeMode === 'auto' ? systemMode : themeMode

    const setThemeMode = useCallback((mode) => {
        const next = THEME_MODES.includes(mode) ? mode : 'auto'
        setThemeModeState(next)
        try {
            window.localStorage?.setItem(THEME_MODE_STORAGE_KEY, next)
        } catch {
            /* ignore */
        }
    }, [])

    const theme = useMemo(
        () =>
            createTheme({
                palette: {
                    mode: resolvedMode,
                    background:
                        resolvedMode === 'dark'
                            ? {
                                  default: '#0F1214', // fondo de página — mismo tono que sidebar
                                  paper: '#161B1F' // cards, tablas, inputs
                              }
                            : {
                                  default: '#f0f2f5', // gris Ant Design
                                  paper: '#ffffff'
                              },
                    ...(resolvedMode === 'dark' && {
                        text: {
                            primary: 'rgb(219, 225, 235)',
                            secondary: 'rgba(194, 202, 214, 0.65)',
                            disabled: 'rgba(199, 208, 220, 0.4)'
                        }
                    })
                },
                shape: {
                    borderRadius: 8
                },
                typography: {
                    fontFamily: '"DM Sans", sans-serif',
                    body1: { fontSize: '0.875rem' }, // 14px en vez de 16px
                    body2: { fontSize: '0.8125rem' } // 13px en vez de 14px
                },
                components: {
                    // main.jsx pone overflow-x:clip en html/body, lo que impide que el overflow:hidden
                    // del bloqueo de scroll de MUI llegue al viewport: la barra de scroll sigue
                    // visible pero MUI añade padding-right al body y el contenido se contrae.
                    MuiModal: {
                        defaultProps: { disableScrollLock: true }
                    },
                    // Tablas MUI clásicas: padding de celda compacto por defecto
                    MuiTable: {
                        defaultProps: { size: 'small' }
                    },
                    // DataGrid: filas compactas por defecto (52px → 36px)
                    MuiDataGrid: {
                        defaultProps: { density: 'compact' }
                    },
                    MuiCssBaseline: {
                        // Elimina los spinners de los inputs numéricos en Firefox, Chrome y Edge.
                        styleOverrides: {
                            'input[type="number"]': {
                                MozAppearance: 'textfield'
                            },
                            'input[type="number"]::-webkit-outer-spin-button, input[type="number"]::-webkit-inner-spin-button':
                                {
                                    WebkitAppearance: 'none',
                                    margin: 0
                                }
                        }
                    },
                    MuiInputBase: {
                        styleOverrides: {
                            input: {
                                // WORKAROUND: Esto evita que el fondo de los inputs se vuelva amarillo
                                // cuando el navegador autocompleta un valor (p.ej. usuario guardado para login).
                                '&:-webkit-autofill, &:-webkit-autofill:hover, &:-webkit-autofill:focus, &:-webkit-autofill:active':
                                    {
                                        WebkitBoxShadow:
                                            '0 0 0 1000px transparent inset !important',
                                        boxShadow: '0 0 0 1000px transparent inset !important',
                                        caretColor: 'currentColor',
                                        transition: 'background-color 9999s ease-in-out 0s',
                                        borderRadius: 'inherit'
                                    },
                                // WORKAROUND: Esto evita que en iOS se haga zoom cuando el usuario enfoca un input con fontSize < 16px.
                                // En vez de reducir el fontSize, escalamos el input al 87.5% (14px) manteniendo su fontSize real a 16px para evitar el zoom.
                                // Excluimos los inputs de tipo color
                                '&:not([type="color"])': {
                                    fontSize: 16,
                                    transform: 'scale(0.875)',
                                    transformOrigin: 'left center'
                                }
                            }
                        }
                    },
                    // MuiAlert: {
                    //     styleOverrides: {
                    //         root: ({ theme }) => ({
                    //             ...(theme.palette.mode === 'dark' && {
                    //                 color: theme.palette.text.primary,

                    //                 '& .MuiAlert-icon': {
                    //                     color: 'inherit'
                    //                 }
                    //             })
                    //         })
                    //     }
                    // },
                    // Varios DialogTitle meten un IconButton de cerrar con position:absolute
                    // (top/right) que por defecto se posiciona respecto al Paper (el único
                    // ancestro con position:relative), no respecto al propio DialogTitle. Al
                    // añadir el paddingTop de safe-area al Paper (ver MuiDialog abajo), el texto
                    // del título baja pero ese botón se queda pegado al borde real. Forzando
                    // position:relative aquí, el botón pasa a posicionarse respecto al
                    // DialogTitle y baja junto con el texto.
                    MuiDialogTitle: {
                        styleOverrides: {
                            root: { position: 'relative' }
                        }
                    },
                    // Los Dialog se vuelven full-screen en mobile (breakpoint down('sm')), así que
                    // el paper necesita el padding del notch/home-indicator del iPhone en la PWA.
                    // Se aplica al Paper (no a DialogTitle/DialogActions) para que funcione igual
                    // sin importar si el diálogo usa esos subcomponentes o una cabecera custom.
                    MuiDialog: {
                        styleOverrides: {
                            paper: ({ theme }) => ({
                                [theme.breakpoints.down('sm')]: {
                                    margin: 0,
                                    width: '100% !important',
                                    maxWidth: '100% !important',
                                    maxHeight: '100%',
                                    height: '100%',
                                    borderRadius: 0,
                                    paddingTop: 'env(safe-area-inset-top)',
                                    paddingBottom: 'env(safe-area-inset-bottom)'
                                }
                            })
                        }
                    },
                    // Los Drawer temporales (anchor left/right) siempre ocupan toda la altura del
                    // viewport (top:0 a bottom:0), tocando notch y home-indicator independientemente
                    // de su ancho — a diferencia de Dialog, esto no depende del breakpoint.
                    MuiDrawer: {
                        styleOverrides: {
                            paper: {
                                paddingTop: 'env(safe-area-inset-top)',
                                paddingBottom: 'env(safe-area-inset-bottom)'
                            }
                        }
                    }
                }
            }),
        [resolvedMode]
    )

    const contextValue = useMemo(
        () => ({
            sidebarColors: resolvedMode === 'dark' ? darkSidebarColors : lightSidebarColors,
            themeMode,
            resolvedMode,
            setThemeMode
        }),
        [resolvedMode, themeMode, setThemeMode]
    )

    return (
        <ThemeModeContext.Provider value={contextValue}>
            <MuiThemeProvider theme={theme}>
                <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={i18n.language}>
                    {children}
                </LocalizationProvider>
            </MuiThemeProvider>
        </ThemeModeContext.Provider>
    )
}
